import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { Tour } from "../../lib/apiTyped";
import { useAuth, type EffectiveRole } from "../../hooks/useAuth";
import { stepsForRole, type TourStep } from "./tourSteps";
import { TourOverlay } from "./TourOverlay";

export type TourContextValue = {
  active: boolean;
  currentStep: TourStep | null;
  currentIndex: number;
  totalSteps: number;
  isFirst: boolean;
  isLast: boolean;
  start: (overrideRole?: EffectiveRole) => void;
  next: () => void;
  prev: () => void;
  skip: () => void;
  complete: () => void;
};

export const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: React.ReactNode }) {
  const { effectiveRole, impersonation } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [steps, setSteps] = useState<TourStep[]>([]);
  const [index, setIndex] = useState(0);
  // Guard so we don't fire `Tour.complete` twice in a row (e.g. user mashing
  // Skip + the network being slow). Tour completion is idempotent server-side
  // but UI state changes shouldn't depend on the response order.
  const completingRef = useRef(false);

  const currentStep = active ? steps[index] ?? null : null;

  // Drive the route from the active step. If the user navigated away
  // manually mid-tour, this snaps the URL back to where the step needs to
  // live. Steps without a `route` (welcome / cta-finish) leave the URL alone.
  useEffect(() => {
    if (!active || !currentStep?.route) return;
    if (window.location.pathname !== currentStep.route) {
      navigate(currentStep.route);
    }
  }, [active, currentStep, navigate]);

  const close = useCallback(
    async (opts: { skipped: boolean }) => {
      if (completingRef.current) return;
      completingRef.current = true;
      setActive(false);
      setIndex(0);
      try {
        await Tour.complete({ skipped: opts.skipped });
      } catch {
        // Best-effort: UI already closed. The flag will reconcile on the
        // next session or via onSnapshot from another tab.
      } finally {
        completingRef.current = false;
      }
    },
    [],
  );

  const start = useCallback(
    (overrideRole?: EffectiveRole) => {
      // Admins impersonating somebody else must never trigger the tour:
      // the `tour-complete` endpoint would write to the admin's own user
      // doc, and the spotlight would highlight items they aren't really
      // exploring. Settings hides the entry point in that case too.
      if (impersonation) return;
      const role = overrideRole ?? effectiveRole;
      if (
        role !== "enterprise_owner" &&
        role !== "enterprise_member" &&
        role !== "institution"
      ) {
        return;
      }
      setSteps(stepsForRole(role));
      setIndex(0);
      setActive(true);
    },
    [effectiveRole, impersonation],
  );

  const next = useCallback(() => {
    setIndex((i) => {
      if (i >= steps.length - 1) {
        void close({ skipped: false });
        return i;
      }
      return i + 1;
    });
  }, [close, steps.length]);

  const prev = useCallback(() => {
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  const skip = useCallback(() => {
    void close({ skipped: true });
  }, [close]);

  const complete = useCallback(() => {
    void close({ skipped: false });
  }, [close]);

  const value = useMemo<TourContextValue>(
    () => ({
      active,
      currentStep,
      currentIndex: index,
      totalSteps: steps.length,
      isFirst: index === 0,
      isLast: index === steps.length - 1,
      start,
      next,
      prev,
      skip,
      complete,
    }),
    [active, currentStep, index, steps.length, start, next, prev, skip, complete],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {active && currentStep && <TourOverlay step={currentStep} />}
    </TourContext.Provider>
  );
}
